import { FC, useState, useEffect } from 'react';
import { useFormik } from 'formik';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import TextField from 'src/components/InputFields/TextField';
import ProductSelect from 'src/components/InputFields/ProductPicker';
import CreatedDatePicker from 'src/components/InputFields/DatePicker';
import { editProduction } from 'src/services/production.service';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  productionDialogSubtitles,
  productionDialogTitles,
  DATE_FORMAT,
} from 'src/utils/properties.utils';
import { productionValidation } from 'src/utils/validationScheme.utils';
import ProductionDialog from 'src/components/DialogBox/ProductionBox/ProductionDialog';
import { Production } from 'src/models/Management.interface';
import dayjs from 'dayjs';
import {
  DateLabel,
  ErrorHelperText,
  FlexContainer,
  FlexColumnContainer,
  FlexGapContainer,
  FlexItem,
} from '../StyledComponents/StyledPageComponents';
import ErrorIcon from 'src/components/InputFields/ErrorIcon';

interface ProductionEditDialogProps {
  open: boolean;
  production: Production | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface ProductionEditFormValues {
  productId: string;
  producedQuantity: string;
  damagedQuantity: string;
  productionDate: string | Date | null;
}

const ProductionEditDialog: FC<ProductionEditDialogProps> = ({
  open,
  production,
  onClose,
  onSuccess,
}) => {
  const formik = useFormik({
    initialValues: {
      productId: '',
      producedQuantity: '',
      damagedQuantity: '',
      productionDate: '' as string | Date | null,
    },
    validationSchema: productionValidation,
    onSubmit: (values) => handleSubmit(values),
  });
  const { showSnackbar } = StyledSnackbar();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (production && open) {
      formik.resetForm({
        values: {
          productId: production.productId,
          producedQuantity: production.producedQuantity.toString(),
          damagedQuantity: production.damagedQuantity.toString(),
          productionDate: production.productionDate
            ? dayjs(production.productionDate).format(DATE_FORMAT)
            : '',
        },
      });
    }
  }, [production, open]);

  const formatDate = (date: string | Date): string => {
    return dayjs(date).format(DATE_FORMAT);
  };

  const handleSubmit = async (values: ProductionEditFormValues) => {
    setLoading(true);

    try {
      const response = await editProduction({
        id: production!.id,
        productId: values.productId,
        producedQuantity: Number(values.producedQuantity),
        damagedQuantity: Number(values.damagedQuantity),
        productionDate: values.productionDate
          ? formatDate(values.productionDate)
          : '',
      });
      showSnackbar(response.message, 'success');
      onSuccess();
      handleClose();
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <FlexContainer>
      <ProductionDialog
        width="840px"
        height="500px"
        open={open}
        handleClose={handleClose}
        title={productionDialogTitles.EDIT}
        subtitle={productionDialogSubtitles.EDIT}
        onSubmit={formik.handleSubmit}
        buttonDisabled={loading}
        buttonLoading={loading}
        buttonLabel="Update"
      >
        <FlexColumnContainer>
          <FlexGapContainer mt={1.5}>
            <FlexItem>
              <ProductSelect
                color="var(--subtext)"
                width="100%"
                id="productId"
                label="Product"
                formikProps={formik}
                showLabel={true}
                errorState={
                  formik.submitCount > 0 && Boolean(formik.errors.productId)
                }
              />
            </FlexItem>
            <FlexItem mt={0.5}>
              <DateLabel>Production Date</DateLabel>
              <CreatedDatePicker
                name="productionDate"
                width="100%"
                height="40px"
                fontSize="16px"
                placeholder="Select production date"
                formikProps={formik}
                errorState={
                  formik.submitCount > 0 &&
                  Boolean(formik.errors.productionDate)
                }
              />
              {(formik.touched.productionDate || formik.submitCount > 0) &&
                formik.errors.productionDate && (
                  <ErrorHelperText marginTop="-13px">
                    <ErrorIcon />
                    {String(formik.errors.productionDate)}
                  </ErrorHelperText>
                )}
            </FlexItem>
          </FlexGapContainer>
          <FlexGapContainer mt={2} mb={5}>
            <FlexItem>
              <TextField
                showLabel={true}
                color="var(--subtext)"
                width="100%"
                id="producedQuantity"
                label="Produced Quantity"
                formikProps={formik}
              />
            </FlexItem>
            <FlexItem>
              <TextField
                showLabel={true}
                color="var(--subtext)"
                width="100%"
                id="damagedQuantity"
                label="Damaged Quantity"
                formikProps={formik}
              />
            </FlexItem>
          </FlexGapContainer>
        </FlexColumnContainer>
      </ProductionDialog>
    </FlexContainer>
  );
};

export default ProductionEditDialog;
